import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, isBackingOff, msUntilBackoffEnds } from "../api/coingecko";

/**
 * Cache-aware fetch hook built on the api/coingecko layer.
 *
 * fetcher: (opts) => Promise<any>   (e.g. fetchMarkets)
 * deps:    array — refetch when these change
 * pollMs:  optional — also refetch on this interval (visibility-aware)
 *
 * Returns { data, error, loading, refresh, rateLimited, retryInMs }.
 * Keeps last good data on error (never clears it), exposes 429 state.
 */
export default function useFetch(fetcher, deps, { pollMs = 0, enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [retryAt, setRetryAt] = useState(null);

  const controllerRef = useRef(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const firstRunRef = useRef(true);

  const run = useCallback(
    async (force = false) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      setLoading(true);
      setError(null);
      try {
        const result = await fetcherRef.current({ signal: controller.signal, force });
        if (controller.signal.aborted) return;
        setData(result);
        setRetryAt(null);
      } catch (err) {
        if (controller.signal.aborted || err?.name === "AbortError") return;
        if (err instanceof ApiError && err.status === 429) {
          setRetryAt(Date.now() + (isBackingOff() ? msUntilBackoffEnds() : 4000));
        } else {
          setRetryAt(null);
        }
        setError(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    []
  );

  // Initial + deps-driven refetch.
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    if (firstRunRef.current) {
      firstRunRef.current = false;
      run();
    } else {
      run();
    }
    return () => controllerRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  // Polling (hooked by usePolling semantics: visibility + pause handled by caller)
  useEffect(() => {
    if (!pollMs || !enabled) return undefined;
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      run();
    }, pollMs);
    return () => clearInterval(id);
  }, [pollMs, enabled, run]);

  const refresh = useCallback(() => run(true), [run]);

  return { data, error, loading, refresh, retryAt };
}
